import { createReducer, createActions } from 'reduxsauce'
import Immutable, { type ImmutableObject, type Immutable as TImmutable } from 'seamless-immutable'
import { PharmacyType } from '@dialogue/coredata'

import RemoteRequestHelper from '../Lib/RemoteRequestHelper'
import { createSelector } from 'APP/Redux/utils'
import { calculateAge } from 'APP/Lib/DateHelpers'
import { PatientState, type NormalizedEpharmacy } from '../Store/Patient/types'

// External pharmacy service considers anyone under 16 years of age a dependent who cannot consult for themselves
export const MINIMUM_ELIGIBLE_EPHARMACY_AGE = 16

/* ------------- Types and Action Creators ------------- */

type ImmutablePatientState = ImmutableObject<PatientState>

const actionDefs = {
  ...RemoteRequestHelper.actionGenerator('patientCreditCardFetch'),
  ...RemoteRequestHelper.actionGenerator('patientCreditCardCharge'),
  patientCreditCardRegisterRequest: ['token', 'chargeId'],
  patientCreditCardRegisterSuccess: ['data'],
  patientCreditCardRegisterFailure: ['error'],
  checkConsentStatusRequest: null,
  checkConsentStatusSuccess: ['hasCurrentConsentVersion'],
  checkConsentStatusFailure: ['error'],
  scribeV2UserFetchTrigger: null,
  scribeV2UserFetchRequest: null,
  scribeV2UserFetchSuccess: ['profile'],
  scribeV2UserFetchFailure: null,
  scribeV2UserEligibilitiesFetchRequest: null,
  scribeV2UserEligibilitiesFetchSuccess: ['profile'],
  scribeV2UserEligibilitiesFetchFailure: null,
  patientEmailChangeRequest: ['email', 'resend'],
  patientEmailChangeSuccess: null,
  patientEmailChangeFailure: ['error'],
  patientProfileFetchRequest: null,
  patientProfileFetchRequestCancelled: null,
  patientProfileFetchRunning: null,
  patientProfileFetchSuccess: ['profile'],
  patientProfileFetchFailure: ['error'],
  patientUpdateLocationSuccess: ['location'],
  patientUpdateLocationFailure: ['error'],
  patientProfileMedicalIdFetchRequest: null,
  patientProfileMedicalIdFetchSuccess: ['idCardUrl'],
  patientProfileMedicalIdFetchFailure: ['error'],
  patientProfileClear: null,
  patientProfileGiveConsent: ['forms'],
  patientProfileDeclineConsent: ['forms'],
  patientProfileGiveConsentSuccess: ['profile'],
  patientProfileGiveConsentFailure: ['error'],
  patientProfileRevokeConsent: null,
  patientProfileUpdateRequest: ['patient'],
  patientProfileUpdateSuccess: ['profile'],
  patientProfileUpdateFailure: null,
  patientProfileUpdateRegionRequest: ['adminAreaISOCode'],
  patientProfileBasicUpdateRequest: ['patient'],
  patientSkipIdCard: null,
  patientClearIdCard: null,
  patientIdCardUpdateRequest: ['fileObj'],
  patientIdCardUpdateSuccess: ['idCardUrl'],
  patientIdCardUpdateFailure: ['error'],
  patientLocationPermissionRequest: ['skip'],
  patientLocationServiceAccess: ['skip'],
  patientEnrolWithEnrolmentCodesRequest: ['codes'],
  patientEnrolWithEnrolmentCodesSuccess: ['data'],
  patientEnrolWithEnrolmentCodesFailure: ['error'],
  userInfoFetchRequest: null,
  userInfoFetchSuccess: ['userInfo'],
  userInfoFetchFailure: ['error'],
  unchallengedInvitationSearchRequest: null,
  unchallengedInvitationSearchSuccess: null,
  unchallengedInvitationSearchFailure: null,
  challengedInvitationSearchRequest: null,
  challengedInvitationSearchSuccess: ['results'],
  challengedInvitationSearchFailure: null,
  challengedInvitationQueueRequest: ['queue'],
  challengedInvitationQueuePop: [],
  challengedInvitationQueueNext: [],
  challengedInvitationQueueEmpty: [],
  challengeInvitationRequest: ['challenge', 'uniqueIdentifiers'],
  challengeInvitationSuccess: null,
  challengeInvitationFailure: ['error'],
  resendInvitationRequest: ['email'],
  resendInvitationSuccess: null,
  resendInvitationFailure: ['error'],
  pillwayAuthenticationRequest: ['request'],
  pillwayAuthenticationSuccess: ['accessToken'],
  pillwayAuthenticationFailure: ['error'],
  onboardingDeferredEnrolmentComplete: null,
  setIsOnboarding: ['isOnboarding'],
  postNotificationsRequest: null,
  changeEmailRequest: ['password'],
  setChangeEmailToken: ['token'],
  changeEmailFailure: ['error'],
  changeEmailSuccess: ['email'],
  runStartupAfterChangeEmail: [],
  submitCommitment: null,
  setDisplayCommitmentScreen: ['displayCommitmentScreen'],
  skipEmailVerification: null,
  getExternalClaimsSuccess: ['externalClaims'],
} as const satisfies Record<string, string[] | null>

type TActions = typeof actionDefs
type TPayloads = {}

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

export const PatientTypes = Types

export default Creators

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable<PatientState>({
  profile: {
    location: {},
    contentTags: [],
    healthResources: [],
    creditCard: null,
    eligibleServices: {},
    eligibleBrands: [],
    eligibilityIntervals: [],
    hasFamilyAccess: false,
    cobrands: [],
    mfaRequired: false,
  },
  requestRunning: false,
  error: null,
  idCardSkipped: false,
  idCardUrl: null,
  idCardLocalPath: null,
  giveConsentRunning: false,
  profileFetchRunning: false,
  changeEmailRunning: false,
  changeEmailSuccess: null,
  changeEmailError: null,
  userInfo: null,
  userInfoFetchError: null,
  pillway: {
    running: false,
    accessToken: null,
    error: null,
  },
  externalClaims: {},
  unchallengedInvitationSearchRunning: false,
  challengedInvitationSearchRunning: false,
  enrolWithEnrolmentCodesResult: null,
  challengedInvitationQueue: [],
  challengeInvitationRunning: false,
  challengeInvitationError: null,
  isOnboarding: false,
  patientCreditCardRegisterRunning: false,
  patientCreditCardRegisterSuccess: false,
  patientCreditCardRegisterFailure: false,
  changeEmailToken: {},
  isSturtupRunning: false,
  displayCommitmentScreen: false,
  checkConsentStatusRunning: false,
  hasCurrentConsentVersion: null,
  consentStatusError: null,
  requests: {},
})

/* ------------- Reducers ------------- */

// reset profile redux to initial state
export const profileClear = (state: ImmutablePatientState): ImmutablePatientState =>
  INITIAL_STATE.merge({
    changeEmailToken: state.changeEmailToken,
  })

export const setIsSturtupRunning = (
  state: ImmutablePatientState,
  action: { data?: boolean }
): ImmutablePatientState => state.merge({ isSturtupRunning: action.data ?? true })

// start request
export const request = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ requestRunning: true })

export const success = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ requestRunning: false })

// request failed
export const failure = (
  state: ImmutablePatientState,
  action: { error: string | null }
): ImmutablePatientState => state.merge({ requestRunning: false, error: action.error })

// profile fetched successfully
export const profileChangeSuccess = (
  state: ImmutablePatientState,
  action: { profile: Partial<PatientState['profile']> }
): ImmutablePatientState => {
  const newProfile = state.profile.merge(action.profile)
  return state.merge({ requestRunning: false, error: null, profile: newProfile })
}

// medical id fetched successfully
export const patientMedicalIdFetchSuccess = (
  state: ImmutablePatientState,
  action: { idCardUrl: string | null }
): ImmutablePatientState => {
  return state.merge({ idCardUrl: action.idCardUrl })
}

// user has given consent
// FIXME consent data format
export const profileGiveConsent = (state: ImmutablePatientState): ImmutablePatientState => {
  const newProfile = state.profile.merge({
    hasConsented: true,
    consentDate: new Date(),
  })

  return state.merge({
    profile: newProfile,
    giveConsentRunning: true,
  })
}

export const profileGiveConsentSuccess = (
  state: ImmutablePatientState,
  action: { profile: Partial<PatientState['profile']> }
): ImmutablePatientState => {
  const newProfile = state.profile.merge(action.profile)
  return state.merge({
    giveConsentRunning: false,
    error: null,
    profile: newProfile,
    hasCurrentConsentVersion: true,
  })
}

export const profileGiveConsentFailure = (
  state: ImmutablePatientState,
  { error }: { error: string | null }
): ImmutablePatientState => {
  const newProfile = state.profile.merge({
    hasConsented: false,
    hasConsentedLocally: false,
  })
  return state.merge({ giveConsentRunning: false, error: error, profile: newProfile })
}

// consent revoked
// FIXME consent data format
export const profileRevokeConsent = (state: ImmutablePatientState): ImmutablePatientState => {
  const newProfile = state.profile.merge({
    hasConsented: false,
    consentDate: null,
  })
  return state.merge({ profile: newProfile })
}

// basic profile updated successfully
export const profileUpdateRequest = (
  state: ImmutablePatientState,
  action: { patient: Partial<PatientState['profile']> }
): ImmutablePatientState => {
  const profile = state.profile.merge(action.patient)
  return state.merge({ requestRunning: true, profile: profile })
}

// update region directly
export const profileUpdateRegionRequest = (
  state: ImmutablePatientState,
  action: { adminAreaISOCode: string | null }
): ImmutablePatientState => {
  const profile = state.profile.merge({ adminAreaISOCode: action.adminAreaISOCode })
  return state.merge({ profile: profile })
}

// location data has changed
export const updateLocationSuccess = (
  state: ImmutablePatientState,
  action: { location: PatientState['profile']['location'] }
): ImmutablePatientState => {
  const updatedProfile = state.profile.merge({
    locationRequestRunning: false,
    location: action.location,
  })
  return state.merge({ profile: updatedProfile })
}

// location data was not available
export const updateLocationFailure = (
  state: ImmutablePatientState,
  action: { error: unknown }
): ImmutablePatientState => {
  const updatedProfile = state.profile.merge({
    locationRequestRunning: false,
    // workaround to pass error to UI as location
    location: action.error as Record<string, unknown>,
  })
  return state.merge({ profile: updatedProfile })
}

// set skip id card flag
export const skipIdCard = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ idCardSkipped: true })

// unset skip id card flag
export const clearIdCard = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ idCardSkipped: false })

export const idCardUpdateRequest = (
  state: ImmutablePatientState,
  action: { path: string }
): ImmutablePatientState => state.merge({ requestRunning: true, idCardLocalPath: action.path })

export const idCardUpdateSuccess = (
  state: ImmutablePatientState,
  action: { idCardUrl: string | null }
): ImmutablePatientState => state.merge({ requestRunning: false, idCardUrl: action.idCardUrl })

export const profileLocationSuccess = (
  state: ImmutablePatientState,
  action: { idCardUrl: string | null }
): ImmutablePatientState => state.merge({ requestRunning: false, idCardUrl: action.idCardUrl })

export const profileLocationFailure = (
  state: ImmutablePatientState,
  action: { idCardUrl: string | null }
): ImmutablePatientState => state.merge({ requestRunning: false, idCardUrl: action.idCardUrl })

export const profileFetchRequest = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ requestRunning: true, profileFetchRunning: true })

export const profileFetchSuccess = (
  state: ImmutablePatientState,
  action: { profile: Partial<PatientState['profile']> }
): ImmutablePatientState =>
  profileChangeSuccess(state.merge({ profileFetchRunning: false }), action)

export const profileFetchFailure = (
  state: ImmutablePatientState,
  action: { error: string | null }
): ImmutablePatientState => failure(state.merge({ profileFetchRunning: false }), action)

export const cancelProfileFetchRequest = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ requestRunning: false, profileFetchRunning: false })

export const scribeV2UserFetchSuccess = (
  state: ImmutablePatientState,
  action: {
    profile: PatientState['profile']
  }
): ImmutablePatientState => {
  return state.merge({
    profile: state.profile.merge({
      healthResources: action.profile.healthResources,
      contentTags: action.profile.contentTags,
      creditCard: action.profile.creditCard,
      eligibleServices: action.profile.eligibleServices,
      eligibleBrands: action.profile.eligibleBrands,
      hasFamilyAccess: action.profile.hasFamilyAccess,
      cobrands: action.profile.cobrands,
      authId: action.profile.authId,
    }),
  })
}

export const scribeV2UserFetchFailure = (state: ImmutablePatientState): ImmutablePatientState => {
  return state.merge({
    profile: state.profile.merge({
      healthResources: [],
      contentTags: [],
      creditCard: null,
      eligibleServices: {},
      eligibleBrands: [],
      hasFamilyAccess: false,
      cobrands: [],
    }),
  })
}

export const scribeV2UserEligibilitiesFetchSuccess = (
  state: ImmutablePatientState,
  action: {
    profile: {
      isMfaRequired: boolean
      isForcedSessionInactivityRequired?: boolean
      eligibilityIntervals: PatientState['profile']['eligibilityIntervals']
    }
  }
): ImmutablePatientState => {
  return state.merge({
    profile: state.profile.merge({
      mfaRequired: action.profile.isMfaRequired,
      forcedSessionInactivityRequired: action.profile.isForcedSessionInactivityRequired,
      eligibilityIntervals: action.profile.eligibilityIntervals,
    }),
  })
}
export const scribeV2UserEligibilitiesFetchFailure = (
  state: ImmutablePatientState
): ImmutablePatientState => {
  return state.merge({
    profile: state.profile.merge({
      mfaRequired: false,
    }),
  })
}

export const creditCardFetchSuccess = (
  state: ImmutablePatientState,
  action: {
    data?: {
      cardholder_name: string
      brand: string
      last_4_digits: string
      expiration_year: string
      expiration_month: string
    }
  }
): ImmutablePatientState => {
  if (!action.data) return state
  return state.merge({
    profile: state.profile.merge({
      creditCard: {
        name: action.data.cardholder_name,
        brand: action.data.brand,
        last4Digits: action.data.last_4_digits,
        expirationYear: action.data.expiration_year,
        expirationMonth: action.data.expiration_month,
      },
    }),
  })
}

export const emailChangeRequest = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ changeEmailRunning: true, changeEmailError: null })

export const emailChangeSuccess = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ changeEmailRunning: false, changeEmailError: null })

export const emailChangeFailure = (
  state: ImmutablePatientState,
  action: { error: string | null }
): ImmutablePatientState =>
  state.merge({ changeEmailRunning: false, changeEmailError: action.error })

export const userInfoFetchSuccess = (
  state: ImmutablePatientState,
  action: { userInfo: Record<string, unknown> | null }
): ImmutablePatientState => state.merge({ userInfo: action.userInfo, userInfoFetchError: null })

export const userInfoFetchFailure = (
  state: ImmutablePatientState,
  action: { error: unknown }
): ImmutablePatientState => state.merge({ userInfoFetchError: action.error })

export const pillwayAuthenticationRequest = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ pillway: { running: true } })

export const pillwayAuthenticationSuccess = (
  state: ImmutablePatientState,
  action: { accessToken: string | null }
): ImmutablePatientState =>
  state.merge({ pillway: { accessToken: action.accessToken, running: false, error: null } })

export const pillwayAuthenticationFailure = (
  state: ImmutablePatientState,
  action: { error: string | null }
): ImmutablePatientState =>
  state.merge({ pillway: { accessToken: null, running: false, error: action.error } })

export const getExternalClaimsSuccess = (
  state: ImmutablePatientState,
  action: { externalClaims: Record<string, unknown> }
): ImmutablePatientState => state.merge({ externalClaims: action.externalClaims })

export const unchallengedInvitationSearchRequest = (
  state: ImmutablePatientState
): ImmutablePatientState => state.merge({ unchallengedInvitationSearchRunning: true })

export const unchallengedInvitationSearchSuccess = (
  state: ImmutablePatientState
): ImmutablePatientState => state.merge({ unchallengedInvitationSearchRunning: false })

export const unchallengedInvitationSearchFailure = (
  state: ImmutablePatientState
): ImmutablePatientState => state.merge({ unchallengedInvitationSearchRunning: false })

export const challengedInvitationSearchRequest = (
  state: ImmutablePatientState
): ImmutablePatientState => state.merge({ challengedInvitationSearchRunning: true })

export const challengedInvitationSearchSuccess = (
  state: ImmutablePatientState
): ImmutablePatientState => state.merge({ challengedInvitationSearchRunning: false })

export const challengedInvitationSearchFailure = (
  state: ImmutablePatientState
): ImmutablePatientState => state.merge({ challengedInvitationSearchRunning: false })

export const challengedInvitationQueueRequest = (
  state: ImmutablePatientState,
  action: { queue: unknown[] }
): ImmutablePatientState =>
  state.merge({ challengedInvitationQueue: action.queue, challengeInvitationError: null })

export const challengedInvitationQueuePop = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({
    challengedInvitationQueue: state.challengedInvitationQueue.slice(1),
    challengeInvitationError: null,
  })

export const challengedInvitationQueueEmpty = (
  state: ImmutablePatientState
): ImmutablePatientState =>
  state.merge({ challengedInvitationQueue: [], challengeInvitationError: null })

export const enrolWithEnrolmentCodesSuccess = (
  state: ImmutablePatientState,
  action: { data?: { organization: unknown }[] }
): ImmutablePatientState => {
  return state.merge({
    enrolWithEnrolmentCodesResult:
      action?.data?.map?.((enrolmentResult) => enrolmentResult.organization) ?? [],
    requestRunning: false,
  })
}

export const challengeInvitationRequest = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ challengeInvitationRunning: true, challengeInvitationError: null })

export const challengeInvitationSuccess = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({ challengeInvitationRunning: false, challengeInvitationError: null })

export const challengeInvitationFailure = (
  state: ImmutablePatientState,
  action: { error: string | null }
): ImmutablePatientState =>
  state.merge({ challengeInvitationRunning: false, challengeInvitationError: action.error })

export const setIsOnboarding = (
  state: ImmutablePatientState,
  { isOnboarding }: { isOnboarding: boolean }
): ImmutablePatientState => {
  return state.merge({ isOnboarding })
}

export const patientCreditCardRegisterRequest = (
  state: ImmutablePatientState
): ImmutablePatientState => {
  return state.merge({
    patientCreditCardRegisterRunning: true,
    patientCreditCardRegisterSuccess: false,
    patientCreditCardRegisterFailure: false,
  })
}

export const patientCreditCardRegisterSuccess = (
  state: ImmutablePatientState,
  action: {
    data: {
      cardholder_name: string
      brand: string
      last_4_digits: string
      expiration_year: string
      expiration_month: string
    }
  }
): ImmutablePatientState => {
  return state.merge({
    patientCreditCardRegisterRunning: false,
    patientCreditCardRegisterSuccess: true,
    patientCreditCardRegisterFailure: false,
    profile: state.profile.merge({
      creditCard: {
        name: action.data.cardholder_name,
        brand: action.data.brand,
        last4Digits: action.data.last_4_digits,
        expirationYear: action.data.expiration_year,
        expirationMonth: action.data.expiration_month,
      },
    }),
  })
}

export const patientCreditCardRegisterFailure = (
  state: ImmutablePatientState,
  action: { error: boolean }
): ImmutablePatientState => {
  return state.merge({
    patientCreditCardRegisterRunning: false,
    patientCreditCardRegisterFailure: action.error,
  })
}

export const changeEmailSuccess = (
  state: ImmutablePatientState,
  { email }: { email: string }
): ImmutablePatientState => {
  return state.merge({
    profile: { email },
    error: null,
    requestRunning: false,
    changeEmailSuccess: true,
    changeEmailToken: {},
  })
}

export const changeEmailFailure = (
  state: ImmutablePatientState,
  { error }: { error: string | null }
): ImmutablePatientState => {
  return state.merge({
    error: error,
    requestRunning: false,
    changeEmailSuccess: false,
    changeEmailToken: {},
  })
}

export const setChangeEmailToken = (
  state: ImmutablePatientState,
  { token }: { token: Record<string, unknown> }
): ImmutablePatientState => state.merge({ changeEmailToken: token })

export const setDisplayCommitmentScreen = (
  state: ImmutablePatientState,
  { displayCommitmentScreen }: { displayCommitmentScreen: boolean }
): ImmutablePatientState => {
  return state.merge({ displayCommitmentScreen })
}

export const submitCommitment = (state: ImmutablePatientState): ImmutablePatientState => {
  return state.merge({ displayCommitmentScreen: false })
}

export const checkConsentStatusRequest = (state: ImmutablePatientState): ImmutablePatientState =>
  state.merge({
    checkConsentStatusRunning: true,
    consentStatusError: null,
  })

export const checkConsentStatusSuccess = (
  state: ImmutablePatientState,
  { hasCurrentConsentVersion }: { hasCurrentConsentVersion: boolean }
) =>
  state.merge({
    checkConsentStatusRunning: false,
    hasCurrentConsentVersion: hasCurrentConsentVersion,
    consentStatusError: null,
  })

// @ts-expect-error FIXME: state, error typing is not strongly typed
export const checkConsentStatusFailure = (state, { error }) =>
  state.merge({
    checkConsentStatusRunning: false,
    hasCurrentConsentVersion: null,
    consentStatusError: error,
  })

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer<TPayloads, TActions, ImmutablePatientState>(INITIAL_STATE, {
  ...RemoteRequestHelper.handlerGenerator<PatientState>(
    'PATIENT_CREDIT_CARD_FETCH',
    null,
    creditCardFetchSuccess,
    null
  ),
  ...RemoteRequestHelper.handlerGenerator<PatientState>(
    'PATIENT_CREDIT_CARD_CHARGE',
    null,
    null,
    null
  ),
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_CREDIT_CARD_REGISTER_REQUEST]: patientCreditCardRegisterRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_CREDIT_CARD_REGISTER_SUCCESS]: patientCreditCardRegisterSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_CREDIT_CARD_REGISTER_FAILURE]: patientCreditCardRegisterFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SCRIBE_V2_USER_FETCH_SUCCESS]: scribeV2UserFetchSuccess,
  [Types.SCRIBE_V2_USER_FETCH_FAILURE]: scribeV2UserFetchFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PILLWAY_AUTHENTICATION_REQUEST]: pillwayAuthenticationRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PILLWAY_AUTHENTICATION_SUCCESS]: pillwayAuthenticationSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PILLWAY_AUTHENTICATION_FAILURE]: pillwayAuthenticationFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_EXTERNAL_CLAIMS_SUCCESS]: getExternalClaimsSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SCRIBE_V2_USER_ELIGIBILITIES_FETCH_SUCCESS]: scribeV2UserEligibilitiesFetchSuccess,
  [Types.SCRIBE_V2_USER_ELIGIBILITIES_FETCH_FAILURE]: scribeV2UserEligibilitiesFetchFailure,
  [Types.PATIENT_PROFILE_MEDICAL_ID_FETCH_REQUEST]: request,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_MEDICAL_ID_FETCH_SUCCESS]: patientMedicalIdFetchSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_MEDICAL_ID_FETCH_FAILURE]: failure,
  [Types.PATIENT_PROFILE_FETCH_REQUEST]: profileFetchRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_FETCH_SUCCESS]: profileFetchSuccess,
  [Types.PATIENT_PROFILE_FETCH_REQUEST_CANCELLED]: cancelProfileFetchRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_FETCH_FAILURE]: profileFetchFailure,
  [Types.PATIENT_PROFILE_CLEAR]: profileClear,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_GIVE_CONSENT]: profileGiveConsent,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_GIVE_CONSENT_SUCCESS]: profileGiveConsentSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_GIVE_CONSENT_FAILURE]: profileGiveConsentFailure,
  [Types.PATIENT_PROFILE_REVOKE_CONSENT]: profileRevokeConsent,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_UPDATE_REQUEST]: profileUpdateRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_UPDATE_SUCCESS]: profileChangeSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_UPDATE_FAILURE]: failure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_UPDATE_REGION_REQUEST]: profileUpdateRegionRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_PROFILE_BASIC_UPDATE_REQUEST]: profileUpdateRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_UPDATE_LOCATION_SUCCESS]: updateLocationSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_UPDATE_LOCATION_FAILURE]: updateLocationFailure,
  [Types.PATIENT_SKIP_ID_CARD]: skipIdCard,
  [Types.PATIENT_CLEAR_ID_CARD]: clearIdCard,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_EMAIL_CHANGE_REQUEST]: emailChangeRequest,
  [Types.PATIENT_EMAIL_CHANGE_SUCCESS]: emailChangeSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_EMAIL_CHANGE_FAILURE]: emailChangeFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_ID_CARD_UPDATE_REQUEST]: idCardUpdateRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_ID_CARD_UPDATE_SUCCESS]: idCardUpdateSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_ID_CARD_UPDATE_FAILURE]: failure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_ENROL_WITH_ENROLMENT_CODES_REQUEST]: request,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_ENROL_WITH_ENROLMENT_CODES_SUCCESS]: enrolWithEnrolmentCodesSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.PATIENT_ENROL_WITH_ENROLMENT_CODES_FAILURE]: failure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.USER_INFO_FETCH_SUCCESS]: userInfoFetchSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.USER_INFO_FETCH_FAILURE]: userInfoFetchFailure,
  [Types.UNCHALLENGED_INVITATION_SEARCH_REQUEST]: unchallengedInvitationSearchRequest,
  [Types.UNCHALLENGED_INVITATION_SEARCH_SUCCESS]: unchallengedInvitationSearchSuccess,
  [Types.UNCHALLENGED_INVITATION_SEARCH_FAILURE]: unchallengedInvitationSearchFailure,
  [Types.CHALLENGED_INVITATION_SEARCH_REQUEST]: challengedInvitationSearchRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHALLENGED_INVITATION_SEARCH_SUCCESS]: challengedInvitationSearchSuccess,
  [Types.CHALLENGED_INVITATION_SEARCH_FAILURE]: challengedInvitationSearchFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHALLENGED_INVITATION_QUEUE_REQUEST]: challengedInvitationQueueRequest,
  [Types.CHALLENGED_INVITATION_QUEUE_POP]: challengedInvitationQueuePop,
  [Types.CHALLENGED_INVITATION_QUEUE_EMPTY]: challengedInvitationQueueEmpty,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHANGE_EMAIL_REQUEST]: request,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHANGE_EMAIL_SUCCESS]: changeEmailSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHANGE_EMAIL_FAILURE]: changeEmailFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_CHANGE_EMAIL_TOKEN]: setChangeEmailToken,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.RESEND_INVITATION_REQUEST]: request,
  [Types.RESEND_INVITATION_SUCCESS]: success,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.RESEND_INVITATION_FAILURE]: failure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHALLENGE_INVITATION_REQUEST]: challengeInvitationRequest,
  [Types.CHALLENGE_INVITATION_SUCCESS]: challengeInvitationSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHALLENGE_INVITATION_FAILURE]: challengeInvitationFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_IS_ONBOARDING]: setIsOnboarding,
  [Types.RUN_STARTUP_AFTER_CHANGE_EMAIL]: emailChangeRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_DISPLAY_COMMITMENT_SCREEN]: setDisplayCommitmentScreen,
  [Types.SUBMIT_COMMITMENT]: submitCommitment,
  [Types.CHECK_CONSENT_STATUS_REQUEST]: checkConsentStatusRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHECK_CONSENT_STATUS_SUCCESS]: checkConsentStatusSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CHECK_CONSENT_STATUS_FAILURE]: checkConsentStatusFailure,
})

export const selectPatientState = (state: { patient: PatientState }) => state.patient

export const selectPatientProfile = (state: { patient: PatientState }) => state.patient?.profile

export const selectPatientProfileId = (state: { patient: PatientState }) =>
  state.patient?.profile?.id

const hasProvince = (
  location: PatientState['profile']['location']
): location is { latitude: number; longitude: number; province?: string } => {
  return typeof location === 'object' && location !== null && 'province' in location
}

export const selectPatientProfileLocation = createSelector([selectPatientProfile], (profile) => {
  const location = profile?.location
  return location && hasProvince(location) && location.province
    ? `CA-${location.province}`
    : profile?.adminAreaISOCode
})

export const selectPatientProfileLocationCoordinates = createSelector(
  [selectPatientProfile],
  (profile) => {
    const location = profile?.location
    return location && hasProvince(location) && location.latitude && location.longitude
      ? {
          latitude: location.latitude,
          longitude: location.longitude,
        }
      : null
  }
)

export const selectPreferredLanguage = createSelector(
  [selectPatientProfile],
  (profile) => profile?.preferred_language || 'en'
)

export const selectPatientRegion = createSelector([selectPatientProfile], (profile) => {
  return profile?.adminAreaISOCode
})

export const selectExternalClaims = (state: { patient: PatientState }) =>
  state.patient?.externalClaims

export const selectExternalServices = createSelector(
  [selectPatientProfile, selectExternalClaims],
  (profile, externalClaims) => {
    const externalServices = profile?.healthResources
    const age = calculateAge(profile?.dateOfBirth)

    return externalServices?.filter((service): boolean => {
      if (service.type !== PharmacyType.epharmacy) return true
      return !!(age >= MINIMUM_ELIGIBLE_EPHARMACY_AGE && externalClaims?.sunlife?.access_id)
    })
  }
)

export const selectEpharmacyHealthResources = createSelector([selectPatientProfile], (profile) => {
  const ePharmacies = profile?.healthResources?.filter((resource) => resource.type === 'epharmacy')
  // @ts-expect-error Mismatch between immutable and mutable state typings. NB - using a type guard would be much nicer, but doesn't work with seamless-immutable, alas.
  return ePharmacies as TImmutable<NormalizedEpharmacy[]>
})

export const selectEpharmacyServiceByExternalId = createSelector(
  [selectEpharmacyHealthResources, (_, externalId) => externalId],
  (ePharmacies, externalId) => {
    return ePharmacies?.find((service) => service.attributes.epharmacyIdentifier === externalId)
  }
)

export const selectEpharmacyExternalServicesByProvider = createSelector(
  [selectEpharmacyHealthResources, (_, provider) => provider],
  (ePharmacies, provider) =>
    ePharmacies?.filter((service) => service.attributes.epharmacyProvider === provider)
)

export const selectConsentStatus = (state: { patient: PatientState }) => ({
  isLoading: state.patient?.checkConsentStatusRunning ?? false,
  hasCurrentConsentVersion: state.patient?.hasCurrentConsentVersion ?? null,
  error: state.patient?.consentStatusError ?? null,
})

export const selectHasCurrentConsentVersion = (state: { patient: PatientState }) =>
  state.patient?.hasCurrentConsentVersion

export const selectConsentStatusError = (state: { patient: PatientState }) =>
  state.patient?.consentStatusError
